import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft, Save, User, Phone, Mail, MapPin, Hash, Building2, Building, CreditCard } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import api from '../../api/clients';
import { clientSchema } from '../../lib/validation';

type ClientFormData = {
  nombre: string;
  cuil?: string;
  telefono?: string;
  email?: string;
  direccion?: string;
  localidad?: string;
  provincia?: string;
  codigoPostal?: string;
};

export function ClientForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { data: client, isLoading } = useQuery({
    queryKey: ['clients', id],
    queryFn: () => api.get(`/clients/${id}`).then(res => res.data.data),
    enabled: isEditing,
  });

  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
  });

  useEffect(() => {
    if (client && isEditing) {
      setValue('nombre', client.nombre);
      setValue('cuil', client.cuil || '');
      setValue('telefono', client.telefono || '');
      setValue('email', client.email || '');
      setValue('direccion', client.direccion || '');
      setValue('localidad', client.localidad || '');
      setValue('provincia', client.provincia || '');
      setValue('codigoPostal', client.codigoPostal || '');
    }
  }, [client, isEditing, setValue]);

  const mutation = useMutation({
    mutationFn: (data: ClientFormData) => {
      const endpoint = isEditing ? `/clients/${id}` : '/clients';
      const method = isEditing ? 'PUT' : 'POST';
      return api({ method, url: endpoint, data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success(isEditing ? 'Cliente actualizado' : 'Cliente creado');
      navigate('/clientes');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al guardar el cliente');
    },
  });

  const onSubmit = (data: ClientFormData) => {
    mutation.mutate(data);
  };

  if (isLoading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando cliente...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/clientes')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditing ? 'Editar Cliente' : 'Nuevo Cliente'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-xl shadow-sm p-6 max-w-2xl">
        <div className="space-y-4">
          {/* 1. Nombre completo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 items-center gap-2">
              <User size={16} className="text-gray-400" />
              Nombre completo *
            </label>
            <Input
              {...register('nombre')}
              placeholder="Ej: Juan Pérez"
              error={errors.nombre?.message}
            />
          </div>

          {/* 2. CUIL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 items-center gap-2">
              <CreditCard size={16} className="text-gray-400" />
              CUIL
            </label>
            <Input
              {...register('cuil')}
              placeholder="Ej: 20-12345678-9"
              error={errors.cuil?.message}
            />
          </div>

          {/* 3. Teléfono */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 items-center gap-2">
              <Phone size={16} className="text-gray-400" />
              Teléfono
            </label>
            <Input
              {...register('telefono')}
              placeholder="Ej: 11 1234-5678"
              error={errors.telefono?.message}
            />
          </div>

          {/* 4. Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 items-center gap-2">
              <Mail size={16} className="text-gray-400" />
              Email
            </label>
            <Input
              {...register('email')}
              type="email"
              placeholder="Ej: juan@email.com"
              error={errors.email?.message}
            />
          </div>

          {/* 5. Dirección */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 items-center gap-2">
              <MapPin size={16} className="text-gray-400" />
              Dirección
            </label>
            <Input
              {...register('direccion')}
              placeholder="Ej: Av. Tecnología 123"
              error={errors.direccion?.message}
              maxLength={40}
            />
            <p className="text-xs text-gray-400 text-right mt-1">
              {(watch('direccion') || '').length}/40 caracteres
            </p>
          </div>

          {/* 6. Localidad */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 items-center gap-2">
              <Building2 size={16} className="text-gray-400" />
              Localidad
            </label>
            <Input
              {...register('localidad')}
              placeholder="Ej: San Andrés de Giles"
              error={errors.localidad?.message}
            />
          </div>

          {/* 7. Provincia */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 items-center gap-2">
              <Building size={16} className="text-gray-400" />
              Provincia
            </label>
            <Input
              {...register('provincia')}
              placeholder="Ej: Buenos Aires"
              error={errors.provincia?.message}
            />
          </div>

          {/* 8. Código Postal */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 items-center gap-2">
              <Hash size={16} className="text-gray-400" />
              Código Postal
            </label>
            <Input
              {...register('codigoPostal')}
              placeholder="Ej: 6720"
              error={errors.codigoPostal?.message}
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6 pt-6 border-t">
          <Button type="submit" disabled={isSubmitting}>
            <Save size={18} />
            {isSubmitting ? 'Guardando...' : isEditing ? 'Actualizar Cliente' : 'Crear Cliente'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/clientes')}
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
}